import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';

/**
 * Enhanced Subscription Service
 * Handles user subscriptions, trials, Stripe integration, and feature access
 */

// Centralised Premium pricing so UI + Stripe stay in sync.
const PREMIUM_PRICE_EUR = 15; // VAT inclusive monthly price
const PREMIUM_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM || 'price_1SfzvsBomiWO2UmpuJyG7INm';

// Simplified subscription tiers configuration - Free vs Premium
export const SUBSCRIPTION_TIERS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'EUR',
    interval: 'month',
    features: {
      receiptsPerMonth: -1, // unlimited manual entries
      storageGB: 5,
      exportFormats: ['CSV', 'PDF'],
      support: 'Community',
      advancedReporting: false,
      multiCurrency: true,
      apiAccess: false,
      voiceToText: false,
      receiptScanner: false
    },
    description: 'Essential features including profit/tax calculations, pricing calculator, and budget management'
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium',
    price: PREMIUM_PRICE_EUR,
    currency: 'EUR',
    interval: 'month',
    stripeProductId: 'prod_premium_monthly',
    stripePriceId: PREMIUM_PRICE_ID,
    features: {
      receiptsPerMonth: -1, // unlimited
      storageGB: 20,
      exportFormats: ['CSV', 'PDF', 'Excel'],
      support: 'Priority Email',
      advancedReporting: true,
      multiCurrency: true,
      apiAccess: true,
      voiceToText: true,
      receiptScanner: true
    },
    description: 'Voice-to-Text + Receipt Scanner + Advanced Features'
  }
};

// Trial configuration
export const TRIAL_CONFIG = {
  durationDays: 14,
  tier: 'PREMIUM', // Users get Premium features during trial
  gracePeriodDays: 3 // Grace period after trial expires
};

// Lifetime coupon configuration
export const LIFETIME_COUPON = {
  code: '70Lives',
  discountPercent: 70
};

// Legacy support
export const SUBSCRIPTION_PLANS = {
  FREE: 'free',
  PREMIUM: 'premium'
};

export const PREMIUM_FEATURES = {
  VOICE_TO_TEXT: 'voice_to_text',
  RECEIPT_SCANNER: 'receipt_scanner',
  ADVANCED_REPORTS: 'advanced_reports'
};

const PREMIUM_LIKE_STATUSES = new Set([
  'trial',
  'active',
  'past_due',
  'unpaid',
  'incomplete',
  'paused'
]);
const TRIAL_STATUS_ALIASES = new Set([
  'trial',
  'trialing',
  'premium_trial',
  'trial_active'
]);
const VALID_STATUSES = new Set([
  'free',
  'trial',
  'active',
  'past_due',
  'unpaid',
  'incomplete',
  'paused',
  'expired',
  'cancelled'
]);

const pickFirstValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return value;
  }
  return null;
};

const coerceDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value;
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed;
    const match = trimmed.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})$/);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      let year = parseInt(match[3], 10);
      if (year < 100) year += 2000;
      return new Date(year, month, day);
    }
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
};

const resolveTrialDateValue = (data) => (
  coerceDateValue(data?.trialEndDate) ||
  coerceDateValue(data?.subscription?.trialEndDate) ||
  coerceDateValue(data?.subscription?.trialEnd) ||
  coerceDateValue(data?.trialEnd)
);

const normalizeStatusToken = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!normalized) return '';
  if (normalized === 'trialing') return 'trial';
  if (normalized === 'incomplete_expired') return 'expired';
  if (normalized === 'canceled') return 'cancelled';
  if (TRIAL_STATUS_ALIASES.has(normalized)) return 'trial';
  if (VALID_STATUSES.has(normalized)) return normalized;
  if (normalized.includes('premium')) return 'active';
  return normalized;
};

const resolveNormalizedStatus = (data, trialEndDate) => {
  const rawStatus = pickFirstValue(
    data?.subscriptionStatus,
    data?.subscription?.status,
    data?.status
  );
  const statusToken = normalizeStatusToken(rawStatus);
  if (statusToken) {
    if (trialEndDate && trialEndDate > new Date() && (statusToken === 'free' || statusToken === 'expired')) {
      return 'trial';
    }
    return statusToken;
  }

  const rawTier = pickFirstValue(data?.subscriptionTier);
  const tierToken = normalizeStatusToken(rawTier);
  if (tierToken === 'trial') return 'trial';
  if (tierToken === 'active') return 'active';

  const rawPlan = pickFirstValue(data?.subscriptionPlan, data?.subscription?.plan);
  const planToken = normalizeStatusToken(rawPlan);
  if (planToken === 'trial') return 'trial';
  if (planToken === 'active') return 'active';

  if (trialEndDate) {
    return trialEndDate > new Date() ? 'trial' : 'expired';
  }

  return 'free';
};

const normalizePlanToken = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!normalized) return '';
  if (normalized.includes('premium') || normalized.includes('trial')) {
    return SUBSCRIPTION_PLANS.PREMIUM;
  }
  if (normalized.includes('free')) {
    return SUBSCRIPTION_PLANS.FREE;
  }
  return normalized;
};

const resolveNormalizedPlan = (data, normalizedStatus) => {
  const rawPlan = pickFirstValue(
    data?.subscriptionPlan,
    data?.subscription?.plan,
    data?.subscriptionTier
  );
  if (PREMIUM_LIKE_STATUSES.has(normalizedStatus)) {
    return SUBSCRIPTION_PLANS.PREMIUM;
  }
  const planToken = normalizePlanToken(rawPlan);
  if (planToken) {
    return planToken;
  }
  return SUBSCRIPTION_PLANS.FREE;
};

const SUBSCRIPTION_STATUS_SCORE = {
  active: 6,
  past_due: 5,
  unpaid: 5,
  incomplete: 5,
  paused: 4,
  trial: 3,
  free: 2,
  expired: 1,
  cancelled: 1
};

const getSubscriptionScore = (data = {}) => {
  const trialEndValue = resolveTrialDateValue(data);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(data, trialEndDate);
  let score = SUBSCRIPTION_STATUS_SCORE[normalizedStatus] || 0;

  if (data?.stripeSubscriptionId || data?.subscription?.stripeSubscriptionId) score += 3;
  if (data?.subscription?.status || data?.subscriptionStatus) score += 1;
  if (data?.subscription?.currentPeriodEnd || data?.subscriptionCurrentPeriodEnd || data?.subscriptionNextRenewal) score += 1;
  if (data?.subscription?.trialEndDate || data?.trialEndDate) score += 1;

  return { score, normalizedStatus };
};

const buildSubscriptionMigrationUpdate = (primary = {}, secondary = {}) => {
  const update = {};
  const fields = [
    'subscriptionPlan',
    'subscriptionStatus',
    'subscriptionTier',
    'trialStartDate',
    'trialEndDate',
    'trialUsed',
    'subscriptionCurrentPeriodEnd',
    'subscriptionNextRenewal',
    'subscriptionNextBillingDate',
    'stripeCustomerId',
    'stripeSubscriptionId',
    'lastInvoiceId',
    'openInvoiceAmountDue',
    'couponCode',
    'couponRedeemed',
    'couponDiscountPercent',
    'cancelAtPeriodEnd',
    'paymentMethodAddedAt'
  ];

  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(primary, field) && primary[field] !== undefined) {
      update[field] = primary[field];
      return;
    }
    if (Object.prototype.hasOwnProperty.call(secondary, field) && secondary[field] !== undefined) {
      update[field] = secondary[field];
    }
  });

  const subscription = {
    ...(secondary?.subscription || {}),
    ...(primary?.subscription || {})
  };
  if (Object.keys(subscription).length) {
    update.subscription = subscription;
  }

  const trialEndValue = resolveTrialDateValue(primary);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(primary, trialEndDate);
  const normalizedPlan = resolveNormalizedPlan(primary, normalizedStatus);
  update.subscriptionStatus = normalizedStatus;
  update.subscriptionPlan = normalizedPlan;

  if (!update.stripeCustomerId && subscription?.stripeCustomerId) {
    update.stripeCustomerId = subscription.stripeCustomerId;
  }
  if (!update.stripeSubscriptionId && subscription?.stripeSubscriptionId) {
    update.stripeSubscriptionId = subscription.stripeSubscriptionId;
  }

  return update;
};

export const migrateLegacyUserDoc = async (firebaseUid, legacyUid, email = '') => {
  if (!firebaseUid || !legacyUid || firebaseUid === legacyUid) {
    return { migrated: false, reason: 'no_legacy' };
  }

  try {
    const legacyRef = doc(db, 'users', legacyUid);
    const legacySnap = await getDoc(legacyRef);
    if (!legacySnap.exists()) {
      return { migrated: false, reason: 'legacy_missing' };
    }

    const firebaseRef = doc(db, 'users', firebaseUid);
    const firebaseSnap = await getDoc(firebaseRef);
    const legacyData = legacySnap.data() || {};
    const firebaseData = firebaseSnap.exists() ? firebaseSnap.data() : {};

    const legacyScore = getSubscriptionScore(legacyData);
    const firebaseScore = getSubscriptionScore(firebaseData);
    const useLegacyAsPrimary = legacyScore.score > firebaseScore.score;
    const primary = useLegacyAsPrimary ? legacyData : firebaseData;
    const secondary = useLegacyAsPrimary ? firebaseData : legacyData;

    const update = buildSubscriptionMigrationUpdate(primary, secondary);
    const existingLegacyIds = Array.isArray(firebaseData?.legacyUserIds)
      ? firebaseData.legacyUserIds
      : [];
    update.legacyUserIds = Array.from(new Set([...existingLegacyIds, legacyUid]));
    if (!update.email && email) {
      update.email = email.toLowerCase().trim();
    }
    update.updatedAt = serverTimestamp();

    await setDoc(firebaseRef, update, { merge: true });

    return {
      migrated: true,
      source: useLegacyAsPrimary ? 'legacy' : 'firebase',
      legacyUid
    };
  } catch (error) {
    console.warn('Legacy user migration failed:', error);
    return { migrated: false, reason: error.message || 'migration_failed' };
  }
};

/**
 * Create a new user with free plan (robust version)
 */
export const createFreeUser = async (userId, email = '') => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    console.log('≡ƒöä Creating free user for:', normalizedEmail, 'ID:', userId);
    
    const freeUserData = {
      email: normalizedEmail,
      subscriptionPlan: SUBSCRIPTION_PLANS.FREE,
      subscriptionStatus: 'free',
      createdAt: serverTimestamp(),
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      trialStartDate: null,
      trialEndDate: null
    };
    
    console.log('≡ƒÆ╛ Saving free user data to Firestore...');
    await setDoc(doc(db, 'users', userId), freeUserData, { merge: true });
    
    console.log('Γ£à Free user created successfully:', normalizedEmail);
    return freeUserData;
  } catch (error) {
    console.error('Γ¥î Error creating free user:', error);
    throw error;
  }
};

/**
 * Check if email has already used a trial (simplified version)
 */
export const hasEmailUsedTrial = async (email) => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Check in users collection for any user with this email who has had a trial
    const q = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    
    const querySnapshot = await getDocs(q);
    
    for (const doc of querySnapshot.docs) {
      const userData = doc.data();
      // If user has ever had a trial (current or expired), they can't get another
      if (userData.trialStartDate || userData.subscriptionStatus === 'trial' || userData.subscriptionStatus === 'expired' || userData.trialUsed) {
        console.log('≡ƒÜ½ Email has already used trial:', normalizedEmail);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking trial usage:', error);
    // In case of error, allow trial (be permissive for now)
    console.log('ΓÜá∩╕Å Allowing trial due to check error');
    return false;
  }
};

/**
 * Record trial usage in history (simplified - optional)
 */
export const recordTrialUsage = async (userId, email) => {
  try {
    // For now, just log it - we can add database recording later
    console.log('≡ƒô¥ Trial started for:', email, 'User ID:', userId);
  } catch (error) {
    console.error('Error recording trial usage:', error);
    // Don't throw error here as it's just for additional tracking
  }
};

/**
 * Create a new user with trial (robust fallback version)
 */
export const createTrialUser = async (userId, email = '') => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    console.log('≡ƒöä Creating trial user for:', normalizedEmail, 'ID:', userId);
    
    // Simple check if user already exists
    try {
      const existingUser = await getDoc(doc(db, 'users', userId));
      if (existingUser.exists()) {
        const userData = existingUser.data();
        if (userData.trialStartDate || userData.subscriptionStatus === 'trial' || userData.subscriptionStatus === 'expired' || userData.trialUsed) {
          console.log('≡ƒÜ½ User already has trial history');
          throw new Error('This account has already used a free trial.');
        }
      }
    } catch (checkError) {
      console.log('ΓÜá∩╕Å User check failed, proceeding:', checkError.message);
    }
    
    const trialStartDate = new Date();
    const trialEndDate = new Date(trialStartDate.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days from now
    
    const trialUserData = {
      email: normalizedEmail,
      subscriptionPlan: SUBSCRIPTION_PLANS.PREMIUM,
      subscriptionStatus: 'trial',
      trialStartDate: serverTimestamp(),
      trialEndDate: trialEndDate,
      createdAt: serverTimestamp(),
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      trialUsed: true
    };
    
    console.log('≡ƒÆ╛ Saving trial user data to Firestore...');
    
    // Create/update user document with merge to avoid overwriting
    await setDoc(doc(db, 'users', userId), trialUserData, { merge: true });
    
    console.log('Γ£à Trial user created successfully:', normalizedEmail);
    return trialUserData;
  } catch (error) {
    console.error('Γ¥î Error creating trial user:', error);
    throw error;
  }
};

/**
 * Normalize subscription data and handle trial expiry updates
 */
const normalizeSubscriptionData = async (userId, userData) => {
  if (!userData) return null;

  const now = new Date();
  const trialEndValue = resolveTrialDateValue(userData);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(userData, trialEndDate);
  const normalizedPlan = resolveNormalizedPlan(userData, normalizedStatus);
  const trialStartValue = (
    coerceDateValue(userData?.trialStartDate) ||
    coerceDateValue(userData?.subscription?.trialStartDate) ||
    coerceDateValue(userData?.subscription?.trialStart) ||
    coerceDateValue(userData?.trialStart)
  );

  if (normalizedStatus === 'trial' && trialEndDate) {
    
    console.log('Trial check:', {
      now: now.toISOString(),
      trialEnd: trialEndDate.toISOString(),
      expired: now > trialEndDate,
      status: normalizedStatus
    });
    
    if (now > trialEndDate) {
      // Trial expired - downgrade to free but keep trial history
      const expiredData = {
        subscriptionPlan: SUBSCRIPTION_PLANS.FREE,
        subscriptionStatus: 'expired',
        updatedAt: serverTimestamp(),
        // Keep original trial dates for history
        originalTrialStartDate: userData.originalTrialStartDate || userData.trialStartDate || trialStartValue,
        originalTrialEndDate: userData.trialEndDate || trialEndValue
      };
      
      await updateDoc(doc(db, 'users', userId), expiredData);
      
      console.log('Trial expired, downgraded to free:', userData.email);
      
      return {
        ...userData,
        subscriptionStatus: normalizedStatus,
        subscriptionPlan: normalizedPlan,
        trialStartDate: trialStartValue || userData.trialStartDate,
        trialEndDate: trialEndValue || userData.trialEndDate,
        ...expiredData
      };
    } else {
      // Trial still active - calculate remaining time accurately
      const remainingMs = trialEndDate - now;
      const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      
      console.log('Trial active:', {
        email: userData.email,
        daysRemaining: remainingDays,
        trialEnd: trialEndDate.toISOString()
      });
    }
  }
  
  // Normalize coupon fields
  return {
    ...userData,
    subscriptionStatus: normalizedStatus,
    subscriptionPlan: normalizedPlan,
    trialStartDate: trialStartValue || userData.trialStartDate,
    trialEndDate: trialEndValue || userData.trialEndDate,
    couponCode: userData.couponCode || null,
    couponRedeemed: !!userData.couponRedeemed,
    couponDiscountPercent: userData.couponDiscountPercent || 0
  };
};

/**
 * Get user subscription data with accurate trial tracking
 */
export const getUserSubscription = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      // New user - return null, let the app decide whether to create free or trial
      return null;
    }
    
    return await normalizeSubscriptionData(userId, userDoc.data());
  } catch (error) {
    console.error('Error getting user subscription:', error);
    throw error;
  }
};

/**
 * Subscribe to subscription changes in real time
 */
export const subscribeToSubscription = (userId, onChange, onError) => {
  if (!userId) return () => {};
  const userRef = doc(db, 'users', userId);

  return onSnapshot(userRef, async (snapshot) => {
    if (!snapshot.exists()) {
      if (onChange) onChange(null);
      return;
    }

    try {
      const normalized = await normalizeSubscriptionData(userId, snapshot.data());
      if (onChange) onChange(normalized);
    } catch (err) {
      console.error('Error normalizing subscription snapshot:', err);
      if (onError) onError(err);
    }
  }, (error) => {
    console.error('Error listening to subscription:', error);
    if (onError) onError(error);
  });
};

/**
 * Create new user with trial
 */
export const createUserWithTrial = async (userId, email) => {
  try {
    const userData = {
      email: email.toLowerCase(),
      subscriptionPlan: SUBSCRIPTION_PLANS.PREMIUM,
      subscriptionStatus: 'trial',
      trialStartDate: serverTimestamp(),
      trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
      createdAt: serverTimestamp(),
      stripeCustomerId: null,
      stripeSubscriptionId: null
    };
    
    await setDoc(doc(db, 'users', userId), userData);
    console.log('Γ£à Created new user with 14-day trial:', email);
    return userData;
  } catch (error) {
    console.error('Error creating user with trial:', error);
    throw error;
  }
};

/**
 * Check if user has access to a premium feature
 */
export const hasFeatureAccess = (userSubscription, feature) => {
  if (!userSubscription) return false;
  
  const trialEndValue = resolveTrialDateValue(userSubscription);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(userSubscription, trialEndDate);
  const normalizedPlan = resolveNormalizedPlan(userSubscription, normalizedStatus);
  const openInvoiceAmountDue = Number(
    (userSubscription.subscription && userSubscription.subscription.openInvoiceAmountDue) ||
    userSubscription.openInvoiceAmountDue ||
    0
  );
  const hasOpenInvoice = openInvoiceAmountDue > 0;

  // Premium users have access to all features
  if (normalizedPlan === SUBSCRIPTION_PLANS.PREMIUM) {
    // Check if trial is still active or they have active subscription
    if (normalizedStatus === 'trial') {
      if (!trialEndDate) return true;
      return new Date() <= trialEndDate;
    }
    
    // Treat past_due/unpaid/incomplete as active if there is no open invoice
    if (['past_due', 'unpaid', 'incomplete'].includes(normalizedStatus) && !hasOpenInvoice) {
      return true;
    }

    return normalizedStatus === 'active';
  }
  
  // Free users don't have access to premium features
  return false;
};

/**
 * Get days remaining in trial with accurate calculation
 */
export const getTrialDaysRemaining = (userSubscription) => {
  if (!userSubscription) {
    return 0;
  }
  
  const trialEndValue = resolveTrialDateValue(userSubscription);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(userSubscription, trialEndDate);
  if (normalizedStatus !== 'trial' || !trialEndDate) {
    return 0;
  }
  
  const now = new Date();
  const diffTime = trialEndDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

/**
 * Check if user is eligible for a trial (simplified)
 */
export const isTrialEligible = async (userId, email) => {
  try {
    // Check if user ID has already used a trial
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.originalTrialStartDate || userData.trialStartDate || 
          userData.subscriptionStatus === 'trial' || userData.subscriptionStatus === 'expired' || userData.trialUsed) {
        return { eligible: false, reason: 'User has already used a trial' };
      }
    }
    
    return { eligible: true, reason: 'User is eligible for trial' };
  } catch (error) {
    console.error('Error checking trial eligibility:', error);
    // Be permissive - if we can't check, assume eligible
    return { eligible: true, reason: 'Unable to verify, allowing trial' };
  }
};

/**
 * Update user subscription status (for Stripe webhooks)
 */
export const updateSubscriptionStatus = async (userId, status, stripeData = {}) => {
  try {
    const updateData = {
      subscriptionStatus: status,
      updatedAt: serverTimestamp(),
      ...stripeData
    };
    
    // If activating premium subscription, set plan to premium
    if (status === 'active') {
      updateData.subscriptionPlan = SUBSCRIPTION_PLANS.PREMIUM;
    }
    
    await updateDoc(doc(db, 'users', userId), updateData);
    console.log('Γ£à Updated subscription status:', userId, status);
  } catch (error) {
    console.error('Error updating subscription status:', error);
    throw error;
  }
};

/**
 * Check if user is legacy authorized user (for backward compatibility)
 */
export const isLegacyAuthorizedUser = async (email) => {
  try {
    const q = query(
      collection(db, 'authorizedUsers'),
      where('email', '==', email.toLowerCase())
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking legacy authorization:', error);
    return false;
  }
};

/**
 * Get user's subscription display info
 */
export const getSubscriptionDisplayInfo = (userSubscription) => {
  if (!userSubscription) {
    return {
      plan: 'Free',
      status: 'No subscription',
      daysRemaining: 0,
      isTrialActive: false,
      isPremium: false,
      needsBillingAction: false,
      rawStatus: 'free'
    };
  }
  
  const trialEndValue = resolveTrialDateValue(userSubscription);
  const trialEndDate = trialEndValue?.toDate ? trialEndValue.toDate() : trialEndValue;
  const normalizedStatus = resolveNormalizedStatus(userSubscription, trialEndDate);
  const normalizedPlan = resolveNormalizedPlan(userSubscription, normalizedStatus);
  const isPremiumPlan = normalizedPlan === SUBSCRIPTION_PLANS.PREMIUM;
  const openInvoiceAmountDue = Number(
    (userSubscription.subscription && userSubscription.subscription.openInvoiceAmountDue) ||
    userSubscription.openInvoiceAmountDue ||
    0
  );
  const hasOpenInvoice = openInvoiceAmountDue > 0;
  const daysRemaining = normalizedStatus === 'trial' ? getTrialDaysRemaining(userSubscription) : 0;
  const isTrialActive = normalizedStatus === 'trial' && daysRemaining > 0;
  const needsBillingAction = ['past_due', 'unpaid', 'incomplete'].includes(normalizedStatus) && hasOpenInvoice;
  const isPremiumLikeStatus = ['trial', 'active', 'past_due', 'unpaid', 'incomplete', 'paused'].includes(normalizedStatus);
  const hasPremiumAccess = isTrialActive || normalizedStatus === 'active' || (isPremiumPlan && !needsBillingAction);
  
  let plan = 'Free';
  let status = 'Active';
  
  if (normalizedStatus === 'expired') {
    plan = 'Free';
    status = 'Trial expired';
  } else if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
    plan = 'Free';
    status = 'Cancelled';
  } else if (isPremiumPlan || isPremiumLikeStatus) {
    plan = normalizedStatus === 'trial' ? 'Premium Trial' : 'Premium';
    if (isTrialActive) {
      status = `${daysRemaining} days remaining`;
    } else if (normalizedStatus === 'active') {
      status = 'Active';
    } else if (normalizedStatus === 'past_due') {
      status = 'Payment overdue';
    } else if (normalizedStatus === 'unpaid') {
      status = 'Payment unpaid';
    } else if (normalizedStatus === 'incomplete') {
      status = 'Payment incomplete';
    } else if (normalizedStatus === 'paused') {
      status = 'Paused';
    } else {
      status = normalizedStatus ? `Status: ${normalizedStatus}` : status;
    }
  }
  
  return {
    plan,
    status,
    daysRemaining,
    isTrialActive,
    // Keep legacy meaning: Premium access only when active or in an unexpired trial.
    isPremium: hasPremiumAccess,
    needsBillingAction,
    rawStatus: normalizedStatus,
    openInvoiceAmountDue,
    couponDiscountPercent: userSubscription.couponDiscountPercent || 0,
    couponCode: userSubscription.couponCode || null,
    couponRedeemed: !!userSubscription.couponRedeemed
  };
};

/**
 * Enhanced user profile creation with new subscription tiers
 */
export const createEnhancedUserProfile = async (googleUser) => {
  try {
    const userId = googleUser.firebaseUid || googleUser.id;
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    const now = new Date();
    const trialEndDate = new Date(now.getTime() + (TRIAL_CONFIG.durationDays * 24 * 60 * 60 * 1000));
    
    if (!userDoc.exists()) {
      // New user - create profile with trial
      const userData = {
        id: userId,
        email: googleUser.email,
        name: googleUser.name,
        imageUrl: googleUser.imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Enhanced subscription info
        subscription: {
          tier: TRIAL_CONFIG.tier,
          status: 'trial',
          trialStartDate: serverTimestamp(),
          trialEndDate: trialEndDate,
          stripeCustomerId: null,
          stripeSubscriptionId: null
        },
        
        // Legacy compatibility
        subscriptionPlan: SUBSCRIPTION_PLANS.PREMIUM,
        subscriptionStatus: 'trial',
        trialStartDate: serverTimestamp(),
        trialEndDate: trialEndDate,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        
        // Usage tracking
        usage: {
          receiptsThisMonth: 0,
          storageUsedGB: 0,
          lastResetDate: serverTimestamp()
        },
        
        // Preferences
        preferences: {
          currency: 'EUR',
          country: 'IE',
          notifications: {
            email: true,
            trialReminders: true,
            usageAlerts: true
          }
        }
      };
      
      await setDoc(userRef, userData);
      console.log('Γ£à New user profile created with enhanced trial');
      return userData;
      
    } else {
      // Existing user - update last login
      const existingData = userDoc.data();
      await updateDoc(userRef, {
        updatedAt: serverTimestamp(),
        name: googleUser.name, // Update in case it changed
        imageUrl: googleUser.imageUrl
      });
      
      console.log('Γ£à Existing user profile updated');
      return existingData;
    }
    
  } catch (error) {
    console.error('Γ¥î Error creating/updating enhanced user profile:', error);
    throw error;
  }
};

/**
 * Get enhanced subscription with tier features
 */
export const getEnhancedSubscription = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    let subscription = userData.subscription;
    
    // Handle legacy data structure
    if (!subscription && userData.subscriptionPlan) {
      subscription = {
        tier: userData.subscriptionPlan === SUBSCRIPTION_PLANS.PREMIUM ? 'PRO' : 'FREE',
        status: userData.subscriptionStatus || 'free',
        trialStartDate: userData.trialStartDate,
        trialEndDate: userData.trialEndDate,
        stripeCustomerId: userData.stripeCustomerId,
        stripeSubscriptionId: userData.stripeSubscriptionId
      };
    }
    
    // Check if trial has expired
    if (subscription.status === 'trial') {
      const now = new Date();
      const trialEnd = subscription.trialEndDate.toDate ? 
        subscription.trialEndDate.toDate() : 
        new Date(subscription.trialEndDate);
      
      if (now > trialEnd) {
        // Trial expired - downgrade to free
        await updateDoc(userRef, {
          'subscription.status': 'expired',
          'subscription.tier': 'FREE',
          // Legacy compatibility
          subscriptionPlan: SUBSCRIPTION_PLANS.FREE,
          subscriptionStatus: 'expired',
          updatedAt: serverTimestamp()
        });
        
        subscription.status = 'expired';
        subscription.tier = 'FREE';
      }
    }
    
    // Get tier features
    const tierFeatures = SUBSCRIPTION_TIERS[subscription.tier.toUpperCase()];
    
    return {
      ...subscription,
      features: tierFeatures.features,
      tierInfo: tierFeatures,
      usage: userData.usage || { receiptsThisMonth: 0, storageUsedGB: 0 },
      couponCode: userData.couponCode || null,
      couponRedeemed: !!userData.couponRedeemed,
      couponDiscountPercent: userData.couponDiscountPercent || 0
    };
    
  } catch (error) {
    console.error('Γ¥î Error getting enhanced subscription:', error);
    throw error;
  }
};

/**
 * Check subscription limits for new tier system
 */
export const checkEnhancedSubscriptionLimit = async (userId, action, amount = 1) => {
  try {
    const subscription = await getEnhancedSubscription(userId);
    const features = subscription.features;
    const usage = subscription.usage;
    
    switch (action) {
      case 'upload_receipt':
        if (features.receiptsPerMonth === -1) return true; // Unlimited
        return usage.receiptsThisMonth < features.receiptsPerMonth;
        
      case 'storage':
        return usage.storageUsedGB < features.storageGB;
        
      case 'export':
        return features.exportFormats.length > 0;
        
      case 'voice_to_text':
        return features.voiceToText;
        
      case 'receipt_scanner':
        return features.receiptScanner;
        
      case 'advanced_reporting':
        return features.advancedReporting;
        
      case 'api_access':
        return features.apiAccess;
        
      default:
        return true;
    }
    
  } catch (error) {
    console.error('Γ¥î Error checking enhanced subscription limit:', error);
    return false;
  }
};

/**
 * Update usage tracking for new system
 */
export const updateEnhancedUsage = async (userId, action, amount = 1) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const usage = userData.usage || { receiptsThisMonth: 0, storageUsedGB: 0, lastResetDate: serverTimestamp() };
    
    // Check if we need to reset monthly counters
    const now = new Date();
    const lastReset = usage.lastResetDate.toDate ? usage.lastResetDate.toDate() : new Date(usage.lastResetDate);
    const daysSinceReset = (now - lastReset) / (1000 * 60 * 60 * 24);
    
    let updatedUsage = { ...usage };
    
    if (daysSinceReset >= 30) {
      // Reset monthly counters
      updatedUsage.receiptsThisMonth = 0;
      updatedUsage.lastResetDate = serverTimestamp();
    }
    
    // Update usage based on action
    switch (action) {
      case 'upload_receipt':
        updatedUsage.receiptsThisMonth += amount;
        break;
        
      case 'storage':
        updatedUsage.storageUsedGB += amount;
        break;
    }
    
    await updateDoc(userRef, {
      usage: updatedUsage,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Γ£à Enhanced usage updated for ${action}: +${amount}`);
    
  } catch (error) {
    console.error('Γ¥î Error updating enhanced usage:', error);
    throw error;
  }
};

/**
 * Check if user should see upgrade prompt
 */
export const shouldShowUpgradePrompt = async (userId) => {
  try {
    const subscription = await getEnhancedSubscription(userId);
    
    // Show upgrade prompt if:
    // 1. Trial is expiring soon (3 days or less)
    // 2. User is on free tier and hitting limits
    // 3. Trial has expired
    
    if (subscription.status === 'trial') {
      const now = new Date();
      const trialEnd = subscription.trialEndDate.toDate ? 
        subscription.trialEndDate.toDate() : 
        new Date(subscription.trialEndDate);
      const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      return daysRemaining <= 3;
    }
    
    if (subscription.status === 'expired' || subscription.tier === 'FREE') {
      return true;
    }
    
    return false;
    
  } catch (error) {
    console.error('Γ¥î Error checking upgrade prompt:', error);
    return false;
  }
};

/**
 * Apply lifetime coupon (one-time redemption)
 */
export const applyLifetimeCoupon = async (userId, code) => {
  const normalized = (code || '').trim();
  if (normalized.toLowerCase() !== LIFETIME_COUPON.code.toLowerCase()) {
    throw new Error('Invalid coupon code');
  }

  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  if (userData.couponRedeemed) {
    throw new Error('Coupon already redeemed on this account');
  }

  await updateDoc(userRef, {
    couponCode: LIFETIME_COUPON.code,
    couponRedeemed: true,
    couponDiscountPercent: LIFETIME_COUPON.discountPercent,
    couponAppliedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return {
    code: LIFETIME_COUPON.code,
    discountPercent: LIFETIME_COUPON.discountPercent
  };
};
